import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import debounce from 'debounce';
import { Component } from 'react';
import { makeStyles } from 'tss-react/mui';

import { getAllDimensionTypes, getNewDimensionSuggestions } from '~/api/dimensions';
import * as api from '~/api/dimensions';
import FilterSelect, { type Item } from '~/components/filter-select';
import DimensionSuggestionsList from '~/features/dimensions/dimensions-suggestions-list';
import { DimensionsService } from '~/services/dimensions';
import type { ApiTimeDimension, DimensionType, DiscreteDimension, NewDimensionArgs } from '~/types/api-types';
import { DataStatus, Roles } from '~/types/types';

const useStyles = makeStyles()({
  submit: {
    marginTop: '8px',
    padding: '8px 8px',
  },

  form: {
    width: '100%',
  },

  inputControl: {
    padding: '5px',
  },

  textField: {
    height: 'auto',
    marginTop: '4px',
  },

  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
  },
});

interface BuildSelectArgs {
  error?: boolean;
  options: () => Promise<any>;
  label: string;
  name: string;
  onChange: (item: Item) => void;
  shrinkLabel: boolean;
}

interface NewDimensionFormState {
  readonly timeDimensionId: number | null;
  readonly dimensionTypeId: number | null;
  readonly name: string;
  readonly prefix: string;

  readonly timeDimensionIdError: boolean;
  readonly dimensionTypeIdError: boolean;
  readonly nameError: boolean;
  readonly submissionFeedback: { ok: boolean; message: string | null };
  readonly snackbar: { show: boolean; message: string };

  // Null when no attempt to suggest has been made
  // Empty array when no existing dimensions matched
  readonly currentSuggestions: {
    dimensions: DiscreteDimension[];
    totalResults: number;
    status: DataStatus;
  };

  readonly awaitingSubmitResponse: boolean;
  readonly formValid: boolean;
  addConfirmationOpen: boolean;
}

export function checkDimensionExists(dimensions: DiscreteDimension[], newDimension: NewDimensionArgs) {
  if (dimensions === null) {
    return false;
  }

  let { name, timeDimensionId, dimensionType } = newDimension;
  name = name.trim().toLowerCase();
  const compare = (t: DiscreteDimension) =>
    t.timeDimensionId === timeDimensionId && t.dimensionTypeId === dimensionType && t.name.toLowerCase() === name;

  const existing = dimensions.find(compare);
  return existing !== undefined;
}
interface NewDimensionFormProps {
  classes: ReturnType<typeof useStyles>['classes'];
}

export const AcceptableRoles: Readonly<Roles[]> = [Roles.MetadataAdmin];

class NewDimensionFormRaw extends Component<NewDimensionFormProps, NewDimensionFormState> {
  constructor(props: NewDimensionFormProps) {
    super(props);
    this.state = {
      timeDimensionId: null,
      dimensionTypeId: null,
      name: '',
      prefix: '',

      timeDimensionIdError: false,
      dimensionTypeIdError: false,
      nameError: false,
      submissionFeedback: { ok: true, message: null },
      snackbar: { show: false, message: '' },

      currentSuggestions: {
        dimensions: [],
        totalResults: 0,
        status: DataStatus.Uninitialized,
      },
      awaitingSubmitResponse: false,
      formValid: false,
      addConfirmationOpen: false,
    };
  }

  componentDidUpdate = (_: NewDimensionFormProps, prevState: NewDimensionFormState) => {
    if (
      this.state.timeDimensionId !== prevState.timeDimensionId ||
      this.state.dimensionTypeId !== prevState.dimensionTypeId ||
      this.state.name !== prevState.name ||
      this.state.prefix !== prevState.prefix
    ) {
      this.updateSuggestions();
    }
  };

  updateSuggestions = debounce(async () => {
    if (this.state.timeDimensionId !== null || this.state.dimensionTypeId !== null || this.state.name !== '') {
      const args = {
        timeDimensionId: this.state.timeDimensionId,
        dimensionTypeId: this.state.dimensionTypeId,
        name: this.state.name,
        prefix: this.state.prefix,
        limit: null,
      };
      try {
        this.setState({
          currentSuggestions: {
            ...this.state.currentSuggestions,
            status: DataStatus.Waiting,
          },
        });
        const suggestions = await getNewDimensionSuggestions(args);
        this.setState({
          currentSuggestions: { ...suggestions, status: DataStatus.Ok },
        });
      } catch (error) {
        this.setState({
          currentSuggestions: {
            dimensions: [],
            totalResults: 0,
            status: DataStatus.Error,
          },
        });
      }
    } else {
      this.setState({
        currentSuggestions: {
          ...this.state.currentSuggestions,
          status: DataStatus.Uninitialized,
        },
      });
    }
  }, 250);

  checkErrors = (args: NewDimensionArgs) => {
    const timeDimensionIdError = args.timeDimensionId === null;
    const dimensionTypeIdError = args.dimensionType === null;
    const nameError = args.name.trim() === '';

    return {
      timeDimensionIdError,
      dimensionTypeIdError,
      nameError,
      any: () => timeDimensionIdError || dimensionTypeIdError || nameError,
    };
  };

  checkFormValid = (changes: Partial<NewDimensionArgs>) => {
    // Because setState may update state asynchronolously it may be
    // needed to supply the most recent change separately when
    // this function is called directly from an input handler
    const args = { ...this.getCurrentInput(), ...changes };
    if (this.checkErrors(args).any()) {
      this.setState({
        formValid: false,
        submissionFeedback: { ok: true, message: null },
      });
      return false;
    }
    const { currentSuggestions } = this.state;
    // If currentSuggestions is null, it means we were unable to fetch them from the server
    // Assume the new dimension is not a duplicate so user can submit it for server-side validation
    if (checkDimensionExists(currentSuggestions.dimensions, args)) {
      const submissionFeedback = {
        ok: false,
        message: 'Dimensionen existerar redan',
      };
      this.setState({ submissionFeedback, formValid: false });
      return false;
    } else {
      this.setState({
        formValid: true,
        submissionFeedback: { ok: true, message: null },
      });
      return true;
    }
  };

  getCurrentInput = (): NewDimensionArgs => {
    const prefix = this.state.prefix.trim();
    return {
      dimensionType: this.state.dimensionTypeId!,
      name: this.state.name.trim(),
      timeDimensionId: this.state.timeDimensionId!,
      prefix: prefix !== '' ? prefix : this.state.name,
    };
  };

  buildConfirmationDialogue = () => {
    return (
      <Dialog open={this.state.addConfirmationOpen}>
        <DialogContent>
          <DialogContentText>Vill du lägga till dimensionen?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              this.setState({
                awaitingSubmitResponse: false,
                addConfirmationOpen: false,
              })
            }
            color="secondary"
          >
            Avbryt
          </Button>
          <Button
            autoFocus={true}
            color="primary"
            onClick={async () => {
              this.sendDimension(this.getCurrentInput()).then(() =>
                this.setState({
                  awaitingSubmitResponse: false,
                  addConfirmationOpen: false,
                })
              );
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const args = this.getCurrentInput();

    const anyErrors = this.checkErrors(args);
    if (anyErrors.any()) {
      this.setState({
        timeDimensionIdError: anyErrors.timeDimensionIdError,
        dimensionTypeIdError: anyErrors.dimensionTypeIdError,
        nameError: anyErrors.nameError,
        submissionFeedback: {
          ok: false,
          message: 'Obligatoriska fält är inte ifyllda',
        },
      });
      return;
    }
    this.setState({ awaitingSubmitResponse: true, addConfirmationOpen: true });
  };

  sendDimension = async (args: NewDimensionArgs) => {
    try {
      const dimension = await api.createNewDimension(args);
      const dimensions = this.state.currentSuggestions.dimensions;
      const newDimensionsList = [dimension, ...dimensions];
      const totalResults = (this.state.currentSuggestions.totalResults += 1);
      this.setState({
        currentSuggestions: {
          totalResults,
          dimensions: newDimensionsList,
          status: DataStatus.Ok,
        },
        snackbar: { show: true, message: 'Ny dimension skapad!' },
        formValid: false,
      });
      // needs any/unknown to catch is Error
    } catch (error: any) {
      this.setState({
        submissionFeedback: { ok: false, message: error.message },
      });
    }
  };

  private handleTimeDimensionChange = (i: Item) => {
    const id = i === null ? Number.NaN : Number(i.value);
    this.setState({ timeDimensionId: Number.isNaN(id) ? null : id });
    this.checkFormValid({ timeDimensionId: id });
  };

  private handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ name: e.target.value });
    this.checkFormValid({ name: e.target.value });
  };

  private handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ prefix: e.target.value });
    this.checkFormValid({ prefix: e.target.value });
  };

  private handleDimensionTypeChange = (i: Item | null) => {
    const id = i === null ? Number.NaN : Number(i.value);
    this.setState({ dimensionTypeId: Number.isNaN(id) ? null : id });
    this.checkFormValid({ dimensionType: id });
  };

  private async getTimeDimensions() {
    const timeDimensions = await DimensionsService.getSubmissionYears();
    return timeDimensions.map((t: ApiTimeDimension) => ({
      label: t.name,
      value: t.id,
    }));
  }

  private async getDimensionTypes() {
    const dimensionTypes = await getAllDimensionTypes();
    return dimensionTypes.map((t: DimensionType) => ({
      label: t.name,
      value: t.id,
    }));
  }

  private buildFilterSelect = (args: BuildSelectArgs) => {
    return (
      <FilterSelect
        error={args.error}
        defaultOptions={true}
        loadOptions={args.options}
        cacheOptions={false}
        placeholder={''}
        label={args.label}
        name={args.name}
        loadingMessage={() => 'Laddar...'}
        onChange={args.onChange}
        shrinkLabel={args.shrinkLabel}
      />
    );
  };

  private onDeleteDimension = (id: number) => {
    // Easier to just reload suggestions, as the list might otherwise be one element short
    this.updateSuggestions();
  };

  render() {
    const timeDimensionsSelect = this.buildFilterSelect({
      options: this.getTimeDimensions,
      error: this.state.timeDimensionIdError,
      label: 'År',
      name: 'timeDimension',
      onChange: this.handleTimeDimensionChange,
      shrinkLabel: this.state.timeDimensionId !== null,
    });

    const dimensionTypeSelect = this.buildFilterSelect({
      options: this.getDimensionTypes,
      error: this.state.dimensionTypeIdError,
      label: 'Typ',
      name: 'dimensionType',
      onChange: this.handleDimensionTypeChange,
      shrinkLabel: this.state.dimensionTypeId !== null,
    });

    const { submissionFeedback } = this.state;
    const shrinkPrefix = this.state.prefix !== '' || this.state.name !== '' ? { shrink: true } : undefined;

    const classes = this.props.classes;

    return (
      <>
        <Grid item container md={10} sm={10} xs={12}>
          <form className={classes.form} onSubmit={this.submit} noValidate={true}>
            <Grid item container md={12} xs={12} justifyContent="center">
              <Grid item md={4} sm={6} xs={12}>
                <Typography
                  variant="h6"
                  hidden={submissionFeedback.message === null}
                  color={submissionFeedback.ok ? 'inherit' : 'error'}
                >
                  {submissionFeedback.message !== null ? submissionFeedback.message : null}
                </Typography>
              </Grid>
            </Grid>
            <Grid container item xs={12} justifyContent="center">
              <Grid item lg={2} md={2} sm={2} xs={12} className={classes.inputControl}>
                {timeDimensionsSelect}
              </Grid>
              <Grid item lg={2} md={2} sm={2} xs={12} className={classes.inputControl}>
                {dimensionTypeSelect}
              </Grid>
              <Grid item lg={2} md={2} sm={3} xs={12} className={classes.inputControl}>
                <TextField
                  variant="standard"
                  className={classes.textField}
                  fullWidth={true}
                  required={true}
                  label="Namn"
                  onChange={this.handleNameChange}
                  error={this.state.nameError}
                />
              </Grid>
              <Grid item lg={2} md={2} sm={2} xs={12} className={classes.inputControl}>
                <TextField
                  variant="standard"
                  className={classes.textField}
                  fullWidth={true}
                  label="Prefix"
                  placeholder={this.state.name !== '' ? this.state.name : 'Prefix'}
                  onChange={this.handlePrefixChange}
                  InputLabelProps={shrinkPrefix}
                />
              </Grid>
              {this.buildConfirmationDialogue()}
              <Grid item className={classes.inputControl} lg={2} md={3} sm={3} xs={12}>
                <FormControl variant="standard">
                  <Button
                    className={classes.submit}
                    disabled={this.state.awaitingSubmitResponse || !this.state.formValid}
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Skapa dimension
                  </Button>
                  {this.state.awaitingSubmitResponse && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </form>

          <Grid item container xs={12} justifyContent="center">
            <DimensionSuggestionsList
              currentSuggestions={this.state.currentSuggestions}
              onDeleteDimension={this.onDeleteDimension}
            />
          </Grid>
        </Grid>
        <Snackbar
          open={this.state.snackbar.show}
          message={this.state.snackbar.message}
          autoHideDuration={6000}
          onClose={() => this.setState({ snackbar: { show: false, message: '' } })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      </>
    );
  }
}

const Wrapper = () => {
  const { classes } = useStyles();

  return <NewDimensionFormRaw classes={classes} />;
};

export const Dimensions = Wrapper;
